/**
 * v77.99.9 — Parenting-arrangement constants shared between the Child
 * profile editor, the Settings page (household default), and the
 * Dashboard's adaptive-relevance logic.
 *
 * Keep in sync with `PARENTING_ARRANGEMENTS` in /app/backend/server.py.
 */

export const ARRANGEMENT_TYPES = [
  {
    value: "shared_schedule",
    label: "Shared parenting schedule",
    short: "Shared schedule",
    description: "Both parents follow a defined rota. Calendar and handover times are tracked.",
  },
  {
    value: "flexible",
    label: "Flexible parenting arrangement",
    short: "Flexible",
    description: "Informal or ad-hoc — no fixed rota. We'll skip schedule warnings for this child.",
  },
  {
    value: "sole_care",
    label: "Sole care · no shared schedule",
    short: "Sole care",
    description: "One parent is the sole carer. Co-parent prompts are hidden.",
  },
  {
    value: "expense_only",
    label: "Expense-only tracking",
    short: "Expenses only",
    description: "Only tracking expenses with the other parent. Calendar and schedule prompts are hidden.",
  },
  {
    value: "no_active",
    label: "No active parenting arrangement",
    short: "No active arrangement",
    description: "Legal-only / no-contact / parallel parenting. Dashboard runs in a quiet mode.",
  },
  {
    value: "custom",
    label: "Custom arrangement",
    short: "Custom",
    description: "Bespoke setup — describe details in the child's notes.",
  },
];

export const ARRANGEMENT_LABEL = Object.fromEntries(
  ARRANGEMENT_TYPES.map((a) => [a.value, a.label]),
);

export const ARRANGEMENT_SHORT = Object.fromEntries(
  ARRANGEMENT_TYPES.map((a) => [a.value, a.short]),
);

// Auto-derived states the UI should recognise but never offer in the
// selector. `pending_setup` is set by the backend when there's no real
// co-parent yet → soft "finish setting up" nudge instead of a warning.
export const PENDING_SETUP = "pending_setup";

export const PROFILE_LABEL = {
  schedule_focused: "Schedule-focused",
  expense_focused: "Expense-focused",
  sole_parent: "Sole parent",
  mixed: "Mixed arrangement",
  unconfigured: "Setup pending",
};

/**
 * Should the dashboard suppress hard "missing schedule" warnings for
 * this arrangement value? Mirrors `_arrangement_suppresses_warnings`
 * in server.py. Used as a defence-in-depth check on the frontend so
 * older API responses (before the backend rolled) still feel calm.
 */
export function suppressesScheduleWarnings(arrangement) {
  return [
    "flexible", "sole_care", "expense_only", "no_active", "custom",
    PENDING_SETUP,
  ].includes(arrangement);
}
